import React, { useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import Initials from 'components/Initials';
import Dropdown from 'components/Dropdown';
import SideBar from 'components/SideBar';
import {
  CaretdownfilledIcon,
  CloseIcon,
  HambugerIcon,
  LogoutIcon,
  NotificationIcon,
  ProfileIcon,
} from 'components/icons';
import Switch from 'components/Form/Switch';
import Dropdownbody, {
  stylesForDropDown,
} from 'components/Dropdown/dropdownbody';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { useDispatch, useSelector } from 'react-redux';
import { getInitials, publicPaths } from 'libs';
import { checkAccountStatus } from 'store/actions/dashboard';
import { INDEX, ONBOARDING } from 'router/routes';
import { logout } from 'store/actions/auth';
import Fetching from 'components/Fetching';
import { globalStyles } from 'styles';
import { BackArrow } from 'assets/svgs';
import classname from 'classname';
import ReactJoyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { nextprevTour, startTour, stopTour } from 'store/actions/tour';
import styles from './dashboardLayout.module.scss';

export default function DashboardLayout({
  children,
  showBackIcon,
  onGoBack,
  name,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [_, firstPathName] = location.pathname.split('/');
  const pageName = firstPathName === 'dashboard' ? 'overview' : firstPathName;

  const [goLive, setGoLive] = React.useState(true);
  const [menuActive, setMenuActive] = React.useState(false);
  const [showProfile, setShowProfile] = React.useState(false);
  const { businessSetupRequired = true } = useSelector(
    (state) => state.common?.user,
  );
  const { firstName, lastName } = useSelector(
    (state) => state.common?.user?.merchantUser,
  );
  const clickOutRef = React.useRef();
  useOnClickOutside(clickOutRef, () => setMenuActive(false));

  const fetchAccountStatus = React.useCallback(() => {
    dispatch(checkAccountStatus());
  }, [dispatch]);

  React.useEffect(() => {
    if (businessSetupRequired && accountChecker.completed === undefined) {
      fetchAccountStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAccountStatus]);

  const handleChange = (e) => {
    let { value, type, checked } = e.target;
    value = type === 'checkbox' ? checked : value;
    setGoLive(value);
  };

  const { accountChecker } = useSelector((state) => state.dashboard);

  React.useEffect(() => {
    const { pathname } = location;
    if (publicPaths.has(pathname)) {
      history.push(pathname || '/');
      return;
    }
    if (
      businessSetupRequired &&
      accountChecker.completed === false &&
      (pathname !== INDEX || pathname !== ONBOARDING)
    ) {
      history.push(INDEX);
    }
  }, [accountChecker, history, location.pathname]);

  const { inProgress = false } = accountChecker;
  const completed = !businessSetupRequired || accountChecker.completed;

  const { isFirstTime, ...tourConfig } = useSelector((state) => state.tour);

  React.useEffect(() => {
    if (isFirstTime) {
      // dispatch(startTour());
    }
  }, []);

  const handleJoyrideCallback = (data) => {
    const { action, index, type, status, step } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) && tourConfig.run) {
      dispatch(stopTour({ stepIndex: 0 }));
      localStorage.setItem('tourStep', 0);
    } else if (type === EVENTS.STEP_AFTER && index >= 0) {
      if (step?.nextPath) {
        history.push({
          pathname: step.nextPath,
        });
      }
      const stepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      dispatch(nextprevTour({ stepIndex }));
      localStorage.setItem('tourStep', stepIndex);
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      if (step?.nextPath) {
        history.push({
          pathname: step.nextPath,
        });
      }
      const stepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      dispatch(nextprevTour({ stepIndex }));
      localStorage.setItem('tourStep', stepIndex);
    } else if (type === EVENTS.TOOLTIP_CLOSE) {
      dispatch(stopTour());
    }
  };
  return (
    <>
      <ReactJoyride
        {...tourConfig}
        beaconComponent={null}
        continuous
        showSkipButton
        locale={{ skip: 'Skip', next: 'Next', last: 'Done' }}
        hideBackButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: '#fff',
            backgroundColor: '#fff',
            overlayColor: 'rgba(0, 0, 0, 0.4)',
            primaryColor: '#000',
            textColor: '#232340',
            zIndex: 1000,
          },
          tooltip: {
            width: '300px',
            fontSize: '14px',
          },
          buttonNext: {
            flex: 1,
            backgroundColor: '#00E59E',
            color: '#232340',
            fontWeight: 500,
            fontSize: '14px',
            height: '40px',
          },
          buttonSkip: {
            flex: 1,
            backgroundColor: '#F3F5F9',
            color: '#232340',
            fontWeight: 500,
            fontSize: '14px',
            width: '90%',
            marginRight: '20px',
            height: '40px',
          },
        }}
      />
      <div className={`${styles.layout} ${menuActive ? styles.active : ''}`}>
        <SideBar
          menuActive={menuActive}
          setMenuActive={setMenuActive}
          setupComplete={completed}
          ref={clickOutRef}
        />
        <div className={styles.mainContainer}>
          <div className={styles.header}>
            {!menuActive ? (
              <button
                type="button"
                className={styles.hamburger}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuActive(true);
                }}
              >
                <HambugerIcon />
              </button>
            ) : (
              <button
                type="button"
                className={styles.hamburger}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuActive(false);
                }}
              >
                <CloseIcon />
              </button>
            )}
            {showBackIcon ? (
              <div className={styles.backBtn} data-testid="go-back">
                <div className={styles.backBtnSvg} onClick={onGoBack}>
                  <BackArrow />
                </div>
                <h1 className={globalStyles.pageHeading}>{pageName}</h1>
              </div>
            ) : (
              <h1 className={globalStyles.pageHeading}>{pageName}</h1>
            )}
            <div className={styles.headerGroup}>
              {/* <div className={styles.status}>
              <p className={!goLive ? styles.active : ''}>Test</p>
              <Switch checked={goLive} onChange={handleChange} />
              <p className={goLive ? styles.active : ''}>Go Live</p>
            </div> */}
              {/* <button className={styles.notification}>
              <NotificationIcon />
            </button> */}
              <Dropdown
                isOpen={showProfile}
                setIsOpen={setShowProfile}
                header={
                  <div className={styles.switch}>
                    <div className={styles.initialsgroup}>
                      <Initials initials={getInitials(firstName, lastName)} />
                      <p className={styles.initialsgrouptext}>{firstName}</p>
                    </div>
                    <CaretdownfilledIcon />
                  </div>
                }
              >
                <Dropdownbody>
                  {/* <NavLink
                  to="/"
                  className={classname(
                    stylesForDropDown.dropdownitem,
                    stylesForDropDown.link,
                  )}
                >
                  <ProfileIcon />
                  <p className={stylesForDropDown.dropdownitemtext}>Profile</p>
                </NavLink> */}
                  <div
                    onClick={() => dispatch(logout())}
                    className={classname(
                      stylesForDropDown.dropdownitem,
                      stylesForDropDown.stroke,
                    )}
                  >
                    <LogoutIcon />
                    <p className={stylesForDropDown.dropdownitemtext}>Logout</p>
                  </div>
                </Dropdownbody>
              </Dropdown>
            </div>
          </div>
          {inProgress ? (
            <div className={globalStyles.fullHeight}>
              <Fetching />
            </div>
          ) : (
            <div className={styles.content}>
              <div
                className={`${styles.bg}  ${menuActive ? styles.active : ''}`}
              />
              {children}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
